import * as onlineOrderApi from "../api/onlineOrder";
import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { formatObject } from "../common/formatting";
import GradientBg from "../components/GradietBg";
import { useTranslation } from "../i18n";

/**
 * Status constants matching Python OnlineOrderStatus enum values.
 */
const ORDER_STATUS = {
  CREATED: 1,
  OPENED_BY_CUSTOMER: 2,
  PROCESSING_PAYMENT: 3,
  PAYEMENT_FINISHED: 4,
  CANCELLED: 5,
};

/**
 * Description
 * @returns {object}
 */
function useQuery() {
  const { search } = useLocation();

  return React.useMemo(
    () => Object.fromEntries(new URLSearchParams(search)),
    [search],
  );
}

function getRequiredParams(t) {
  return {
    minutesAmount: { title: t("order.minutesAmount"), format: (v) => v },
    priceRub: { title: t("order.cost"), format: (v) => `${v}₽` },
    phoneNumber: {
      title: t("order.phoneNumber"),
      format: (v) => formatObject(v, "phone"),
      optional: true,
    },
    orderId: { title: "", hidden: true },
    paymentUrl: { title: "", hidden: true },
  };
}

/**
 * @typedef RequiredQueryParams
 * @property {string} minutesAmount
 * @property {string} priceRub
 * @property {string} phoneNumber
 * @property {string} orderId
 * @property {string} paymentUrl
 */

/**
 * @param {object} queryParams
 * @returns {queryParams is RequiredQueryParams}
 */
function isValidQueryParams(queryParams, REQUIRED_PARAMS) {
  for (const param in REQUIRED_PARAMS) {
    if (REQUIRED_PARAMS[param].optional) {
      continue;
    }
    if (param in queryParams) {
      continue;
    }
    console.error(`Query param '${param}' wasn't provided`);
    return false;
  }
  return true;
}

const OnlineOrder = () => {
  const { t } = useTranslation();
  const queryParams = useQuery();
  const REQUIRED_PARAMS = getRequiredParams(t);
  /** @type {import("react").StateUpdater<boolean>} */
  const [isCancelled, setIsCancelled] = useState(false);
  /** @type {import("react").StateUpdater<boolean>} */
  const [isLoading, setIsLoading] = useState(true);

  // Check order freshness on mount
  useEffect(() => {
    const inner = async () => {
      if (!("orderId" in queryParams)) {
        setIsLoading(false);
        return;
      }

      try {
        await onlineOrderApi.sendOnlineOrderOpened(queryParams.orderId);
        setIsCancelled(false);
      } catch (err) {
        // Read current_status from the Error object (set by API module)
        const currentStatus = err?.current_status;

        if (currentStatus === ORDER_STATUS.CANCELLED) {
          // Order was cancelled by admin — disable the pay button
          setIsCancelled(true);
        } else {
          // All other cases: double-scan (illegal transition), already paid,
          // network issues, etc. — never show an error banner. Just log it
          // and leave the page functional so the user can retry or navigate away.
          console.warn("sendOnlineOrderOpened failed:", err?.message);
          setIsCancelled(false);
        }
      } finally {
        setIsLoading(false);
      }
    };
    inner();
  }, []);

  const handlePay = useCallback(() => {
    onlineOrderApi
      .sendOnlineOrderPaymentStarted(queryParams.orderId)
      .then(() => {
        console.debug("Order status set to payment started");
        window.location.href = queryParams.paymentUrl;
      })
      .catch((err) => {
        const currentStatus = err?.current_status;

        if (currentStatus === ORDER_STATUS.CANCELLED) {
          setIsCancelled(true);
        } else {
          console.warn("Failed to start payment:", err?.message);
        }
      });
  }, [queryParams.orderId, queryParams.paymentUrl]);

  if (!isValidQueryParams(queryParams, REQUIRED_PARAMS)) {
    return <h1>{t("orderPage.wrongRequest")}</h1>;
  }

  return (
    <GradientBg>
      <div className="px-6 pt-14 min-h-[100vh]">
        <div className="max-w-sm mx-auto">
          <h1 className="text-center text-4xl">
            <b>{t("orderPage.title")}</b>
          </h1>
          <h2 className="text-center mt-4">{t("orderPage.instruction")}</h2>

          <div className="mt-10 flex flex-col items-start gap-6">
            {Object.entries(REQUIRED_PARAMS).map(
              ([paramName, config]) =>
                !config.hidden &&
                paramName in queryParams && (
                  <div key={paramName}>
                    <p className="text-md">{config.title}</p>
                    <h1 className="mt-2 text-3xl font-semibold">
                      {config.format(queryParams[paramName])}
                    </h1>
                  </div>
                ),
            )}
          </div>
          <p className="mt-16 text-center text-xs">
            {t("orderPage.agreement")}{" "}
            <a
              href="https://cyberpass.tech/user-agreement"
              className="underline"
            >
              CyberPass
            </a>
          </p>
          <button
            className="mt-6 bg-white text-[#805BEA] rounded-lg w-full py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handlePay}
            disabled={isCancelled || isLoading}
          >
            {isCancelled
              ? t("orderPage.orderCancelled")
              : isLoading
                ? t("common.loading")
                : t("orderPage.pay")}
          </button>
        </div>
      </div>
    </GradientBg>
  );
};

export default OnlineOrder;
